"use client";

import { LEADERSHIP_INTRO } from "@/data/about-content";
import { useContent } from "@/hooks/useContent";
import type { Bilingual, Locale } from "@/types/home";
import { motion } from "framer-motion";
import { useLocale } from "next-intl";
import { FaLinkedinIn } from "react-icons/fa6";

interface Leader { id: string; name: Bilingual; role: Bilingual; bio: Bilingual; photo: string; linkedin: string; email: string }
interface AboutContent { leaders: Leader[] }

/** LEADERSHIP — accountable people, portrait cards with bios (panel-managed). */
export default function AboutLeadership() {
  const locale = useLocale() as Locale;
  const { data } = useContent<AboutContent>("about");
  const leaders = data?.leaders ?? [];

  if (leaders.length === 0) return null;

  return (
    <section id="leadership" className="scroll-mt-44 border-b border-black/10 dark:border-white/10">
      <div className="mx-auto max-w-360 px-6 py-20 md:py-28 lg:px-10">
        <span className="text-[11px] uppercase tracking-[0.22em] text-accent">{LEADERSHIP_INTRO.eyebrow[locale]}</span>
        <h2 className="mt-3 max-w-2xl font-bebas text-[clamp(2rem,4vw,3.4rem)] uppercase leading-[0.95] text-ash dark:text-white">
          {LEADERSHIP_INTRO.title[locale]}
        </h2>

        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {leaders.map((m, i) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 34 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="group"
            >
              <div className="relative aspect-[4/5] overflow-hidden bg-black/5 dark:bg-white/5">
                {m.photo && (
                  <img
                    src={m.photo}
                    alt={m.name[locale]}
                    className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.04] group-hover:grayscale-0"
                  />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
              </div>
              <h3 className="mt-5 text-xl font-semibold text-ash dark:text-white">{m.name[locale]}</h3>
              <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-accent">{m.role[locale]}</p>
              <p className="mt-3 text-sm leading-relaxed text-ash/55 dark:text-white/55">{m.bio[locale]}</p>
              {(m.linkedin || m.email) && (
                <a
                  href={m.linkedin || `mailto:${m.email}`}
                  aria-label={`${m.name[locale]} — ${m.linkedin ? "LinkedIn" : "Email"}`}
                  className="mt-4 inline-flex h-9 w-9 items-center justify-center border border-black/15 text-ash/70 transition-colors hover:border-accent hover:text-accent dark:border-white/15 dark:text-white/70"
                >
                  <FaLinkedinIn className="h-4 w-4" />
                </a>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
